import { memo, useMemo, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, User, LogOut } from "lucide-react";
import { clearTokenCache } from "@/services/token-service";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { useTranslation } from "@/hooks/useTranslation";

const usePageTitle = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  return useMemo(() => {
    if (pathname === "/") return t("header.TitleDashboard", "Analytics");
    if (pathname?.startsWith("/usage")) return t("header.TitleUsage", "Usage");
    if (pathname?.startsWith("/agents/") && pathname !== "/agents")
      return t("header.TitleAgentPlayground", "Agent Playground");
    if (pathname?.startsWith("/agents"))
      return t("header.TitleAgents", "Agents");
    if (pathname?.startsWith("/messaging"))
      return t("header.TitleMessaging", "Messaging");
    if (pathname?.startsWith("/account"))
      return t("header.TitleAccountSettings", "Account Settings");
    if (pathname?.startsWith("/calendar"))
      return t("header.TitleCalendar", "Calendar");
    return t("header.TitleDashboard", "Analytics");
  }, [pathname, t]);
};

const useBackButton = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();

  return useMemo(() => {
    // Show back button on agent detail pages
    if (pathname?.startsWith("/agents/") && pathname !== "/agents") {
      return {
        show: true,
        onClick: () => navigate("/agents"),
        label: t("header.BackButton", "Back"),
      };
    }

    // Add other pages that need back buttons here
    // if (pathname?.startsWith("/messaging/") && pathname !== "/messaging") {
    //   return {
    //     show: true,
    //     onClick: () => navigate("/messaging"),
    //     label: "Back to Messaging"
    //   };
    // }

    return { show: false };
  }, [pathname, navigate, t]);
};

export const Header = memo(() => {
  const { t } = useTranslation();
  const { user, isLoading, error, logout } = useAuth0();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const pageTitle = usePageTitle();
  const backButton = useBackButton();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".profile-dropdown")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);
  // Render authentication buttons based on user's state
  const renderAuthButtons = () => {
    if (isLoading) return <span>{t("header.Loading", "Loading...")}</span>;
    if (error)
      return (
        <span>
          {t("header.Error", "Error: ")}
          {error.message}
        </span>
      );

    if (user) {
      return (
        <div className="profile-dropdown relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 hover:bg-gray-100 rounded-lg p-2 transition-colors duration-200"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center bg-gray-100">
              <img
                src={user.picture || "/default-avatar.png"}
                alt="Profile"
                className="w-full h-full object-contain"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.src = "/default-avatar.png";
                }}
              />
            </div>
            <span className="text-sm font-medium hidden md:block">
              {user.name}
            </span>
            <ChevronDown
              className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                  <img
                    src={user.picture || "/default-avatar.png"}
                    alt="Profile"
                    className="w-full h-full object-contain"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      e.target.src = "/default-avatar.png";
                    }}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {user.name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                </div>
              </div>
              <Link
                to="/account"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                onClick={() => setIsDropdownOpen(false)}
              >
                <User className="h-4 w-4" />
                {t("header.UserDropdownAccountSettings", "Account Settings")}
              </Link>
              <div className="border-t border-gray-200 my-1"></div>
              <button
                onClick={() => {
                  clearTokenCache();
                  setIsDropdownOpen(false);
                  logout({ logoutParams: { returnTo: window.location.origin } });
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors duration-200 w-full text-left"
              >
                <LogOut className="h-4 w-4" />
                {t("header.UserDropdownLogout", "Logout")}
              </button>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <header className="flex h-14 items-center gap-4 border-b bg-background px-4 lg:h-[60px] lg:px-6">
      <SidebarTrigger />
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center gap-3">
          {backButton?.show && (
            <Button
              variant="ghost"
              size="sm"
              onClick={backButton.onClick}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {backButton.label}
            </Button>
          )}
          <h1 className="text-lg font-semibold">{pageTitle}</h1>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {renderAuthButtons()}
        </div>
      </div>
    </header>
  );
});

Header.displayName = "Header";
